// React
import PropTypes from "prop-types";
import { useContext, useMemo, useState } from "react";

// Apollo
import { gql, useLazyQuery, useMutation, useQuery } from "@apollo/client";

// @mui
import { Card, Grid, Icon, Tooltip, Menu, MenuItem } from "@mui/material";

// UI
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import SinDatos from "components/Carteles/SinDatos";
import Loading from "components/Carteles/Loading";
import { getUproColor } from "utils/colors";
import SoftBadge from "components/SoftBadge";

// Context
import { MessageManager } from "context";
import { ConfirmarContext } from "context/ConfirmarContext";

// Modals
import ModalAddPresupuesto from "./ModalAddPresupuesto";
import ModalAddProductoPresupuesto from "./ModalAddProductoPresupuesto";
import ModalProductosPresupuesto from "./ModalProductosPresupuesto";
import ModalFinalizarPresupuesto from "./ModalFinalizarPresupuesto";
import { formatMoneyPunto } from "utils/formatters";

export default function PresupuestosTab({ idEvento }) {
  const { handleSnackbar } = useContext(MessageManager);
  const { confirmar } = useContext(ConfirmarContext);
  const [openAdd, setOpenAdd] = useState(false);
  const [openAddProducto, setOpenAddProducto] = useState(false);
  const [openProductos, setOpenProductos] = useState(false);
  const [openFinalizar, setOpenFinalizar] = useState(false);
  const [selectedPresupuesto, setSelectedPresupuesto] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [estadoMenuAnchor, setEstadoMenuAnchor] = useState(null);
  // PDF se abre en una pestaña aparte, no se usa generador oculto
  const isFinalizado = (selectedPresupuesto?.estado || "").toLowerCase() === "finalizado";
  const isAprobado = (selectedPresupuesto?.estado || "").toLowerCase() === "aprobado";
  const isBloqueado = isFinalizado || isAprobado;
  const hasVenta = Boolean(selectedPresupuesto?.idVentaEvento);

  const { loading, data, refetch } = useQuery(
    gql`
      query getPresupuestosEvento($filter: JSON) {
        presupuestoEventos(filter: $filter) {
          id
          fecha
          vencimiento
          estado
          lugar
          personal
          monto
          idVentaEvento
          evento {
            id
            nombreEvento
            fechaEvento
            estado
            clienteEvento {
              id
              nombre
              razonSocial
              tipoDocumento
              documento
              tipoCliente
              telefono
              email
            }
          }
          detalles {
            id
            producto {
              id
            }
            idEmpleado
            cantidadAsignada
            precioUnitario
          }
        }
      }
    `,
    {
      variables: {
        filter: {
          idEvento: idEvento,
        },
      },
      fetchPolicy: "network-only",
      skip: !idEvento,
    }
  );

  const presupuestos = useMemo(() => data?.presupuestoEventos || [], [data]);
  // Ya no se carga el PDF por query aquí; se abrirá en la ruta /presupuesto-evento/pdf/:id
  // Consulta mínima para obtener datos necesarios para facturar
  const [loadPresupuestoFactura] = useLazyQuery(
    gql`
      query presupuestoEventoFactura($id: ID!) {
        presupuestoEvento(id: $id) {
          id
          monto
          detalles {
            id
            cantidadAsignada
            precioUnitario
            producto {
              id
            }
          }
          evento {
            id
            clienteEvento {
              id
              tipoDocumento
              tipoResponsable
              documento
            }
          }
        }
      }
    `,
    { fetchPolicy: "network-only" }
  );
  const [deletePresupuesto] = useMutation(
    gql`
      mutation deletePresupuestoEvento($id: ID!) {
        deletePresupuestoEvento(id: $id)
      }
    `
  );
  const [updateEstado] = useMutation(
    gql`
      mutation updateEstadoPresupuesto($input: PresupuestoEventoInput!) {
        savePresupuestoEvento(input: $input) {
          id
          estado
        }
      }
    `
  );
  const [saveVenta, { loading: savingFactura }] = useMutation(
    gql`
      mutation saveVenta($input: VentaEventoInput!) {
        saveVentaEvento(input: $input) { id }
      }
    `
  );

  // Estado via menú agrupado

  const handleOpenMenu = (e, presupuesto) => {
    setSelectedPresupuesto(presupuesto);
    setMenuAnchor(e.currentTarget);
  };
  const handleCloseMenu = () => {
    setMenuAnchor(null);
    setEstadoMenuAnchor(null);
  };
  const handleOpenEstadoSubmenu = (e) => setEstadoMenuAnchor(e.currentTarget);
  const handleCloseEstadoSubmenu = () => setEstadoMenuAnchor(null);
  const handleChangeEstadoDirect = (estado) => {
    const p = selectedPresupuesto;
    if (!p) return handleCloseMenu();
    // Confirmar cambio de estado
    handleCloseMenu();
    const esAprobado = (estado || "").toLowerCase() === "aprobado";
    const mensaje = esAprobado
      ? `¿Marcar el presupuesto #${p.id} como ${estado}? Al aprobar no podrás editar ni Editars, ni cambiar de estado. Esta acción no se puede deshacer.`
      : `¿Marcar el presupuesto #${p.id} como ${estado}?`;
    confirmar({
      title: "Cambiar estado",
      message: mensaje,
      icon: "warning",
      func: () =>
        updateEstado({ variables: { input: { id: p?.id, estado } } })
          .then(() => {
            handleSnackbar("Estado actualizado", "success");
            if (typeof refetch === "function") refetch();
          })
          .catch(() => handleSnackbar("Error al actualizar estado", "error")),
    });
  };
  console.log("Presupuestos:", presupuestos);
  return (
    <SoftBox>
      <SoftBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <SoftTypography variant="h6">Presupuestos</SoftTypography>
        <Tooltip title="Crear presupuesto" placement="top">
          <SoftButton color="primary" circular onClick={() => setOpenAdd(true)}>
            <Icon>add</Icon>
            &nbsp;Nuevo presupuesto
          </SoftButton>
        </Tooltip>
      </SoftBox>

      {loading ? (
        <Loading />
      ) : presupuestos.length === 0 ? (
        <SinDatos />
      ) : (
        <Grid container spacing={2}>
          {presupuestos.map((p) => (
            <Grid item xs={12} md={6} key={p.id}>
              <Card>
                <SoftBox p={2} display="flex" justifyContent="space-between" alignItems="center">
                  <SoftBox>
                    <SoftTypography variant="subtitle2">Presupuesto #{p.id}</SoftTypography>
                    <SoftBox display="flex" alignItems="center" gap={1} mt={0.5}>
                      <SoftBadge
                        color={
                          p?.estado === "Aprobado"
                            ? "uproGreen"
                            : p?.estado === "Pendiente"
                              ? "uproYellow"
                              : p?.estado === "Rechazado"
                                ? "uproPink"
                                : "secondary"
                        }
                        badgeContent={p?.estado || "-"}
                      />
                      <SoftTypography variant="caption" color="text">
                        {p?.lugar || "Sin lugar"}
                      </SoftTypography>
                    </SoftBox>
                    <SoftTypography variant="caption" color="text">
                      Fecha: {p?.fecha || "-"} {p?.vencimiento ? `| Vence: ${p.vencimiento}` : ""}
                    </SoftTypography>
                    <SoftTypography variant="caption" color="text" display="block">
                      {p?.detalles && p?.detalles.length > 0
                        ? `Monto: $ ${formatMoneyPunto(
                          p?.detalles.reduce(
                            (total, d) =>
                              total + (d?.cantidadAsignada || 0) * (d?.precioUnitario || 0),
                            0
                          )
                        )}`
                        : `
                        Monto estimado: ${p?.monto != null ? ` $ ${formatMoneyPunto(p.monto)}` : " -"
                        }`}
                    </SoftTypography>
                    {/* <SoftTypography variant="caption" color="text" display="block">
                      Detalles asignados: {p?.monto != null ? `$ ${p.monto}` : "-"} | Detalles:{" "}
                      {p?.detalles?.length || 0}
                    </SoftTypography> */}
                  </SoftBox>
                  <SoftBox>
                    {/* <Tooltip title="Editar" placement="top">
                      <Icon
                        sx={{
                          verticalAlign: "middle",
                          cursor: "pointer",
                          color: getUproColor("uproGreen"),
                        }}
                        onClick={() => {
                          setSelectedPresupuesto(p);
                          setOpenAddProducto(true);
                        }}
                      >
                        add_shopping_cart
                      </Icon>
                    </Tooltip>
                    <Tooltip title="Ver/Editar productos" placement="top">
                      <Icon
                        sx={{
                          ml: 2,
                          verticalAlign: "middle",
                          cursor: "pointer",
                          color: getUproColor("uproYellow"),
                        }}
                        onClick={() => {
                          setSelectedPresupuesto(p);
                          setOpenProductos(true);
                        }}
                      >
                        inventory
                      </Icon>
                    </Tooltip>
                    <Tooltip title="Cambiar estado" placement="top">
                      <Icon
                        sx={{
                          ml: 2,
                          verticalAlign: "middle",
                          cursor: "pointer",
                          color: getUproColor("uproBlue"),
                        }}
                        onClick={(e) => handleOpenEstadoMenu(e, p)}
                      >
                        flag
                      </Icon>
                    </Tooltip>
                    <Tooltip title="Eliminar presupuesto" placement="top">
                      <Icon
                        sx={{
                          ml: 2,
                          verticalAlign: "middle",
                          cursor: "pointer",
                          color: getUproColor("uproPink"),
                        }}
                        onClick={() => {
                          confirmar({
                            title: "Eliminar presupuesto",
                            message: "¿Está seguro que desea eliminar este presupuesto?",
                            icon: "warning",
                            func: () => {
                              deletePresupuesto({ variables: { id: p?.id } })
                                .then(() => {
                                  handleSnackbar("Presupuesto eliminado", "success");
                                  refetch();
                                })
                                .catch(() => handleSnackbar("Error al eliminar", "error"));
                            },
                          });
                        }}
                      >
                        delete
                      </Icon>
                    </Tooltip> */}
                    <Tooltip title="Acciones" placement="top">
                      <Icon
                        sx={{
                          ml: 2,
                          verticalAlign: "middle",
                          cursor: "pointer",
                          color: getUproColor("secondary"),
                        }}
                        onClick={(e) => handleOpenMenu(e, p)}
                      >
                        more_vert
                      </Icon>
                    </Tooltip>
                  </SoftBox>
                </SoftBox>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <ModalAddPresupuesto
        open={openAdd}
        handleClose={() => setOpenAdd(false)}
        idEvento={idEvento}
        refetch={refetch}
        onCreated={(idNuevo) => {
          // Al crear, abrir directamente el modal para Editars
          setSelectedPresupuesto({ id: idNuevo });
          setOpenAddProducto(true);
        }}
      />

      <ModalAddProductoPresupuesto
        open={openAddProducto}
        handleClose={() => {
          setOpenAddProducto(false);
          setSelectedPresupuesto(null);
        }}
        idPresupuesto={selectedPresupuesto?.id}
        refetch={refetch}
      />

      <ModalProductosPresupuesto
        open={openProductos}
        handleClose={() => {
          setOpenProductos(false);
          setSelectedPresupuesto(null);
        }}
        idPresupuesto={selectedPresupuesto?.id}
        onChanged={refetch}
      />

      <ModalFinalizarPresupuesto
        open={openFinalizar}
        handleClose={() => {
          setOpenFinalizar(false);
          setSelectedPresupuesto(null);
        }}
        idPresupuesto={selectedPresupuesto?.id}
        idEvento={idEvento}
        onFinalized={refetch}
      />

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {!isBloqueado && (
          <MenuItem
            onClick={() => {
              setOpenAddProducto(true);
              handleCloseMenu();
            }}
          >
            <Icon sx={{ mr: 1, color: getUproColor("uproGreen") }}>add_shopping_cart</Icon>
            Editar
          </MenuItem>
        )}
        {/* {!isBloqueado && (
          <MenuItem
            onClick={() => {
              setOpenProductos(true);
              handleCloseMenu();
            }}
          >
            <Icon sx={{ mr: 1, color: getUproColor("uproYellow") }}>inventory</Icon>
            Ver/Editar productos
          </MenuItem>
        )} */}
        {!isBloqueado && (
          <>
            <MenuItem onClick={handleOpenEstadoSubmenu}>
              <Icon sx={{ mr: 1, color: getUproColor("uproBlue") }}>flag</Icon>
              Cambiar estado
              <Icon sx={{ ml: "auto", color: getUproColor("secondary") }}>chevron_right</Icon>
            </MenuItem>
            {/* Submenu de estado */}
            <Menu
              anchorEl={estadoMenuAnchor}
              open={Boolean(estadoMenuAnchor)}
              onClose={handleCloseEstadoSubmenu}
              anchorOrigin={{ vertical: "top", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "left" }}
            >
              <MenuItem
                onClick={() => {
                  handleChangeEstadoDirect("Pendiente");
                  handleCloseMenu();
                }}
              >
                <Icon sx={{ mr: 1, color: getUproColor("uproOrange") }}>schedule</Icon>
                Marcar como Pendiente
              </MenuItem>
              <MenuItem
                onClick={() => {
                  handleChangeEstadoDirect("Aprobado");
                  handleCloseMenu();
                }}
              >
                <Icon sx={{ mr: 1, color: getUproColor("uproGreen") }}>task_alt</Icon>
                Marcar como Aprobado
              </MenuItem>
              <MenuItem
                onClick={() => {
                  handleChangeEstadoDirect("Rechazado");
                  handleCloseMenu();
                }}
              >
                <Icon sx={{ mr: 1, color: getUproColor("error") }}>block</Icon>
                Marcar como Rechazado
              </MenuItem>
            </Menu>
          </>
        )}
        {!isFinalizado && (
          <MenuItem
            onClick={() => {
              const p = selectedPresupuesto;
              if (!p) return handleCloseMenu();
              confirmar({
                title: "Eliminar presupuesto",
                message: "¿Está seguro que desea eliminar este presupuesto?",
                icon: "warning",
                func: () => {
                  deletePresupuesto({ variables: { id: p?.id } })
                    .then(() => {
                      handleSnackbar("Presupuesto eliminado", "success");
                      refetch();
                    })
                    .catch(() => handleSnackbar("Error al eliminar", "error"));
                },
              });
              handleCloseMenu();
            }}
          >
            <Icon sx={{ mr: 1, color: getUproColor("error") }}>delete</Icon>
            Eliminar presupuesto
          </MenuItem>
        )}
        {!isFinalizado && (
          <MenuItem
            disabled={
              !selectedPresupuesto ||
              !(selectedPresupuesto?.detalles || []).some((d) => d?.producto?.id) ||
              !isAprobado
            }
            onClick={() => {
              handleCloseMenu();
              if (selectedPresupuesto) setOpenFinalizar(true);
            }}
          >
            <Icon sx={{ mr: 1, color: getUproColor("uproGreen") }}>done_all</Icon>
            Finalizar presupuesto
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            const p = selectedPresupuesto;
            if (!p) return handleCloseMenu();
            // Abrir PDF en pestaña nueva
            window.open(`/presupuesto-evento/pdf/${p.id}`);
            handleCloseMenu();
          }}
        >
          <Icon sx={{ mr: 1, color: getUproColor("error") }}>picture_as_pdf</Icon>
          Ver/Generar PDF
        </MenuItem>
        {isFinalizado && !hasVenta && (<MenuItem
          onClick={() => {
            const { evento } = selectedPresupuesto || {};
            console.log('Generar factura para presupuesto:', evento);
            if (!evento) return handleCloseMenu();
            if (!evento.clienteEvento || !evento.clienteEvento.id) {
              handleSnackbar("Falta el cliente del evento para facturar", "error");
              return;
            }
            confirmar({
              title: "Generar factura",
              message: `¿Desea generar la factura por $ ${formatMoneyPunto(selectedPresupuesto?.monto)} para el presupuesto #${selectedPresupuesto?.id}?`,
              icon: "warning",
              func: () =>
                saveVenta({
                  variables: {
                    input: {
                      idEvento: evento.id,
                      idPresupuestoEvento: selectedPresupuesto?.id,
                      idClienteEvento: evento.clienteEvento.id,
                      total: selectedPresupuesto?.monto || 0,
                      recargo: 0,
                      pagos: [],
                      idSede: 1, 
                    },
                  },
                })
                  .then(() => {
                    handleSnackbar("Factura generada", "success");
                    if (typeof refetch === "function") refetch();
                  })
                  .catch(() => handleSnackbar("Error al generar factura", "error"))
                  .finally(() => { handleCloseMenu()})
            });
          }}
        >
          <Icon sx={{ mr: 1, color: getUproColor("uproBlue") }}>receipt_long</Icon>
          {savingFactura ? "Generando..." : "Generar factura"}
        </MenuItem>)}
      </Menu>

      {/* PDF ahora se abre en nueva pestaña; se elimina el generador oculto */}
    </SoftBox >
  );
}

PresupuestosTab.propTypes = {
  idEvento: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};
